import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
import { IconDefinitionConstants } from "utils/constants";
import useCommonFilterProps from "./forms/useCommonFilterProps";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, PageLayouts, RouteBreadCrumb } from "components/common";
import AssetsList from "./assetsList";
import useAssetsReportFilterDefinition from "./forms/useAssetsReportFilterDefinition";
import { Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import { useHoldingRegisterService } from "serviceHooks";
import useHoldingRegisterPermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const AssetsReportView: React.FC = () => {
  const holdingRegisterService = useHoldingRegisterService();
  const urlParams = useParams<EntityIdParams>();
  const id = urlParams.id ?? "";

  const { filterFormFields, filterQueryStateHandler } = useAssetsReportFilterDefinition();
  const { initialFilter } = useCommonFilterProps();

  const { hasHoldingRegisterAvailable } = useHoldingRegisterPermissions();

  const substrings = id.split("_");
  const assetAccount = substrings[1] ?? "";
  const depreciatingAccount = substrings[2] ?? "";

  const title = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.HoldingRegister.HoldingRegisterFor,
    "Holding register",
    {
      assetAccount,
      depreciatingAccount,
    }
  );

  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const fileName = `${title} - ${companyName}`;

  const tableRef = React.useRef<TableHandles>(null);

  const exportReportExcel = async () => {
    tableRef.current?.exportToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportToCSV();
  };

  const exportReportPDF = async () => {
    tableRef.current?.exportToPDF();
  };

  return (
    <PageLayouts.DashboardLayout
      key={"assetsReport"}
      entityKey={0}
      title={title}
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={title} />}
      hasPermissions={hasHoldingRegisterAvailable}
      topRightButtons={
        <DashboardButtonGroup
          buttons={[<FilterButton />]}
          moreActions={[
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
              icon: IconDefinitionConstants.WSave,
              onClickHandler: exportReportExcel,
            },
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
              icon: IconDefinitionConstants.WSave,
              onClickHandler: exportReportCSV,
            },
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DownloadAsPDF),
              icon: IconDefinitionConstants.WDownload,
              onClickHandler: exportReportPDF,
            },
          ]}
        />
      }
      filterFields={filterFormFields}
      initialFilter={initialFilter}
      reportToAPIFilterFormatter={holdingRegisterService.assetsReportToAPIFilter}
      filterQueryStateHandler={filterQueryStateHandler}
    >
      <Grid item container>
        <AssetsList ref={tableRef} exportFileName={fileName} holdingRegisterId={id} />
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default AssetsReportView;
